import classNames from 'classnames';
import React, { ReactNode } from 'react';

import { IconArrowLeft, IconChevronRight } from '@pentabd/icons';

import Text from '../text/Text';

import useMediaQuery, { SCREEN_SIZES } from '../../hooks/useMediaQuery';

export interface HeaderProps {
  path?: JSX.Element | JSX.Element[];
  breadcrumbs?: {
    label?: string;
    onClick?: () => void;
    link?: string;
    icon?: ReactNode;
  }[];
  headerText?: {
    header?: string;
    subHeader?: string;
  };
  avatar?: ReactNode;
  actions?: JSX.Element | JSX.Element[];
  className?: string;
  headerClassName?: string;
  size?: 'sm';
  noBorder?: boolean;
}

const Header = ({
  breadcrumbs,
  headerText,
  avatar,
  actions,
  className,
  headerClassName,
  size,
  noBorder,
}: HeaderProps) => {
  const isMobile = useMediaQuery(`(max-width: ${SCREEN_SIZES.MD})`);

  return (
    <div
      className={classNames(
        'd-flex flex-column',
        {
          'border-bottom': !noBorder,
        },
        className,
      )}
    >
      <>
        {Array.isArray(breadcrumbs) &&
          breadcrumbs.length &&
          (isMobile ? (
            <div
              onClick={() => window.history.back()}
              className="d-flex align-items-center gap-4"
            >
              <IconArrowLeft size={20} fill="dark" stroke="subtitle1" />
              <p className="text-subtitle1 fs-sm">Back</p>
            </div>
          ) : (
            <div className="d-flex flex-row gap-6 align-items-center">
              {breadcrumbs.map(({ icon, label, link, onClick }, index) => (
                <React.Fragment key={index}>
                  {icon && icon}
                  {link ? (
                    <a
                      href={link}
                      className="text-subtitle2 text-decoration-none py-2 px-4 rounded-2 fs-sm"
                    >
                      {label}
                    </a>
                  ) : onClick ? (
                    <a
                      onClick={() => onClick()}
                      className="text-subtitle2 text-decoration-none py-2 px-4 rounded-2 fs-sm pointer"
                    >
                      {label}
                    </a>
                  ) : (
                    <>
                      {label && (
                        <p className="text-subtitle2 py-2 px-4 rounded-2 fs-sm bg-light">
                          {label}
                        </p>
                      )}
                    </>
                  )}
                  {index !== breadcrumbs.length - 1 && (
                    <IconChevronRight size="20" fill="dark" />
                  )}
                </React.Fragment>
              ))}
            </div>
          ))}
      </>

      {headerText && (
        <div
          className={classNames({ 'py-12': !headerClassName }, headerClassName)}
        >
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-6">
            <div
              className={classNames('d-flex align-items-center', {
                'gap-8': size === 'sm',
                'gap-10': size !== 'sm',
              })}
            >
              {avatar && avatar}
              <div>
                <p
                  className={classNames({
                    'fw-semibold fs-lg text-dark lh-lg': size === 'sm',
                    'fw-bold display-sm lh-display-sm mb-4 text-title':
                      size !== 'sm',
                  })}
                >
                  {headerText.header}
                </p>
                <Text size={size === 'sm' ? 'sm' : 'md'} color="subtitle1">
                  {headerText.subHeader}
                </Text>
              </div>
            </div>
            <div>
              {Array.isArray(actions) && actions.length && (
                <div className="d-flex flex-wrap gap-6 align-items-center">
                  {actions.map((action, id) => (
                    <React.Fragment key={id}>{action}</React.Fragment>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Header;
